import { InfoToggle } from '../components/InfoToggle';
import { Unavailable } from '../components/Unavailable';
import { useConsensusStatus } from '../lib/consensus-status';

/**
 * The chain tip, from the consensus node's own `/status`, shared with the
 * connection button. `/state/root-hash/verified` isn't used: it reads the same
 * app hash from the same node, a step further away, and its parser has been
 * failing on the node's `/block` answer (HTTP 503 "CometBFT error").
 */
export function StatePanel() {
  const {
    latestBlock,
    latestBlockTime,
    latestAppHash,
    latencyMs,
    error,
    hasLoaded,
    refresh,
  } = useConsensusStatus();
  const hasData = latestBlock !== null || latestAppHash !== null;

  return (
    <section className="panel" data-testid="chain-state">
      <h2>
        Chain state
        <InfoToggle>
          The state root is the app hash in the latest block, as the consensus node reports it,
          with that block's height and time. Refreshes every 15 seconds.
        </InfoToggle>
      </h2>

      {!hasLoaded && <p className="subtle">Loading…</p>}

      {hasLoaded && !hasData && (
        <Unavailable onRetry={refresh} testid="state-error">
          Chain state isn't available right now. We'll keep trying.
        </Unavailable>
      )}

      {hasData && (
        <div className="result" data-testid="state-result">
          <div className="result-header">
            {error ? (
              <span className="badge badge-warn" data-testid="state-stale">
                last known
              </span>
            ) : (
              <span className="badge badge-ok">✓ connected</span>
            )}
            <div className="result-header-right">
              {error ? (
                <span className="result-latency">Reconnecting…</span>
              ) : (
                latencyMs !== null && <span className="result-latency">{latencyMs} ms</span>
              )}
            </div>
          </div>
          <div className="result-body">
            <dl className="kv-list">
              <dt>state root</dt>
              <dd>
                <code data-testid="state-root">{latestAppHash ?? '—'}</code>
              </dd>
              {latestBlock !== null && (
                <>
                  <dt>block height</dt>
                  <dd>
                    <code data-testid="state-height">{latestBlock.toLocaleString()}</code>
                  </dd>
                </>
              )}
              {latestBlockTime && (
                <>
                  <dt>last block</dt>
                  <dd>{new Date(latestBlockTime).toLocaleTimeString()}</dd>
                </>
              )}
            </dl>
          </div>
        </div>
      )}
    </section>
  );
}
